import { useCallback, useEffect, useRef } from "react";

import { languageMap } from "../components/settings/AudioTracks";
import { useMediaContext } from "../contexts/MediaContext";
import { usePrevious } from "../helpers/commonUtils";
import { usePlayerStore } from "../store/usePlayerStore";
import {
  AdbMediaApi,
  StateName,
  StateObject,
  Tracker
} from "../types/analytics";
import { AudioTrack } from "../types/common";
import {
  OndemandAudioTrack,
  ScheduledProgram,
  Subtitles
} from "../types/player-api";

const resolveAudioDescriptionLanguage = (language: string | undefined) => {
  switch (language) {
    case "qfi":
      return "fin";
    case "qen":
      return "eng";
    case "qsv":
    case "qsw":
      return "swe";
    default:
      return "und";
  }
};

const resolveAudioTrackType = (
  audioTrack?: AudioTrack | OndemandAudioTrack
): string => {
  const language = audioTrack?.language;
  const type = audioTrack?.type;
  if (type) {
    return type.toLowerCase();
  }
  return language &&
    language.length === 3 &&
    !languageMap[language] &&
    language.charAt(0) === "q"
    ? "audiodescription"
    : "audiotrack";
};

const resolveAudioTrackLanguage = (
  audioTrack?: AudioTrack | OndemandAudioTrack
): string => {
  const language = audioTrack?.language;
  if (language) {
    if (languageMap[language]) {
      return language;
    }
    for (const [key, value] of Object.entries(languageMap)) {
      if (language === value) {
        return key;
      }
    }
  }
  return resolveAudioDescriptionLanguage(language);
};

const resolveTextTrackLanguage = (subtitles?: Subtitles): string => {
  for (const [key, value] of Object.entries(languageMap)) {
    if (subtitles?.language === value) {
      return key;
    }
  }
  return "und";
};

const resolveTextTrackKind = (subtitles?: Subtitles): string => {
  switch (subtitles?.label) {
    case "käännöstekstitys":
    case "översättning":
    case "subtitles":
      return "translation";
    case "ohjelmatekstitys":
    case "programtextning":
      return "hardofhearing";
    default:
      return "unknown";
  }
};

const isSeekingToNewProgram = (
  currentTime: number,
  serviceSchedule: Omit<ScheduledProgram, "audioTracks" | "subtitles">,
  seekableEnd?: number
) => {
  const millisecondsInPast =
    currentTime && currentTime > 0 && seekableEnd && seekableEnd > 0
      ? (seekableEnd - currentTime) * 1000
      : 0;
  const targetTime = Date.now() - millisecondsInPast;
  return (
    Date.parse(serviceSchedule.endTime) > targetTime &&
    Date.parse(serviceSchedule.startTime) < targetTime
  );
};

export const useAdobeCustomStates = (
  tracker: Tracker | null,
  adbMediaApi: AdbMediaApi | undefined
) => {
  const { getState, useStoreState } = usePlayerStore();
  const isCasting = useStoreState.isCasting();
  const isFullscreen = useStoreState.isFullscreen();
  const isLive = useStoreState.isLive();
  const isMuted = useStoreState.isMuted();
  const isSeeking = useStoreState.isSeeking();
  const scheduledProgram = useStoreState.scheduledProgram();
  const selectedAudioTrackId = useStoreState.selectedAudioTrackId();
  const selectedSubtitles = useStoreState.selectedSubtitles();

  const audioTrackState = useRef<StateObject>();
  const fullscreenState = useRef<StateObject>();
  const muteState = useRef<StateObject>();
  const textTrackState = useRef<StateObject>();

  const prevIsSeeking = usePrevious(isSeeking);

  const { audioTracks: audioTracksFromApi } = useMediaContext();

  const handleState = useCallback(
    (
      previousState: StateObject,
      startNewState: boolean,
      stateName: StateName
    ) => {
      if (tracker && adbMediaApi && !isCasting) {
        if (previousState) {
          tracker.trackEvent(adbMediaApi.Event.StateEnd, previousState);
        }
        if (startNewState) {
          const newState = adbMediaApi.createStateObject(stateName);
          tracker.trackEvent(adbMediaApi.Event.StateStart, newState);
          return newState;
        }
        return undefined;
      }
    },
    [adbMediaApi, isCasting, tracker]
  );

  const handleAudioTrackStateChange = useCallback(
    (_selectedAudioTrackId: number) => {
      const selectedAudioTrack =
        _selectedAudioTrackId === -1 && audioTracksFromApi
          ? (audioTracksFromApi[0] as OndemandAudioTrack)
          : getState().audioTracks.find(e => e.id === _selectedAudioTrackId);
      const audioTrackStateName = `audio.${resolveAudioTrackLanguage(selectedAudioTrack)}.${resolveAudioTrackType(selectedAudioTrack)}`;
      audioTrackState.current = handleState(
        audioTrackState.current,
        true,
        audioTrackStateName
      );
    },
    [audioTracksFromApi, getState, handleState]
  );

  const handleAudioTrackStateEnd = useCallback(() => {
    if (!tracker || !adbMediaApi) return;
    if (audioTrackState.current) {
      tracker.trackEvent(adbMediaApi.Event.StateEnd, audioTrackState.current);
      audioTrackState.current = undefined;
    }
    if (textTrackState.current) {
      tracker.trackEvent(adbMediaApi.Event.StateEnd, textTrackState.current);
      textTrackState.current = undefined;
    }
  }, [adbMediaApi, tracker]);

  useEffect(() => {
    // Handle audioTrack state change after user has changed the audioTrack
    if (!getState().isSeeking && selectedAudioTrackId !== undefined) {
      handleAudioTrackStateChange(selectedAudioTrackId);
    }
  }, [getState, handleAudioTrackStateChange, selectedAudioTrackId]);

  useEffect(() => {
    // Handle audioTrack state start after seek (only with live)
    const _selectedAudioTrackId = getState().selectedAudioTrackId;
    if (
      isLive &&
      prevIsSeeking &&
      !isSeeking &&
      !audioTrackState.current &&
      _selectedAudioTrackId !== undefined
    ) {
      handleAudioTrackStateChange(_selectedAudioTrackId);
    }
  }, [getState, handleAudioTrackStateChange, isLive, isSeeking, prevIsSeeking]);

  useEffect(() => {
    // Handle audioTrack state end during seek (only with live)
    if (
      isLive &&
      isSeeking &&
      scheduledProgram &&
      !isSeekingToNewProgram(
        getState().currentTime,
        scheduledProgram,
        getState().seekableEnd
      )
    ) {
      handleAudioTrackStateEnd();
    }
  }, [getState, handleAudioTrackStateEnd, isLive, isSeeking, scheduledProgram]);

  useEffect(() => {
    muteState.current = handleState(muteState.current, isMuted, "mute");
  }, [handleState, isMuted]);

  useEffect(() => {
    fullscreenState.current = handleState(
      fullscreenState.current,
      isFullscreen,
      "fullScreen"
    );
  }, [handleState, isFullscreen]);

  useEffect(() => {
    // On live, handle text track change after seek
    if (isLive && !isSeeking && prevIsSeeking && !textTrackState.current) {
      const _selectedSubtitles = getState().selectedSubtitles;
      textTrackState.current = handleState(
        textTrackState.current,
        !!_selectedSubtitles && _selectedSubtitles.kind !== "off",
        `text.${resolveTextTrackLanguage(_selectedSubtitles)}.${resolveTextTrackKind(_selectedSubtitles)}`
      );
    }
  }, [getState, handleState, isLive, isSeeking, prevIsSeeking]);

  useEffect(() => {
    // Handle state change on text track change after user has changed the text track
    if (!isLive || !getState().isSeeking) {
      textTrackState.current = handleState(
        textTrackState.current,
        !!selectedSubtitles && selectedSubtitles.kind !== "off",
        `text.${resolveTextTrackLanguage(selectedSubtitles)}.${resolveTextTrackKind(selectedSubtitles)}`
      );
    }
  }, [getState, handleState, isLive, selectedSubtitles]);
};
